import React, { useState, useEffect, useRef } from 'react';
import config from '../config.json';
import ImageDropUpload from '../components/ImageDropUpload';
import { useNavigate } from 'react-router-dom';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Home 페이지 편집 컴포넌트
function EditHome() {
  const navigate = useNavigate();
  const imageUploadRef = useRef();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // 데이터 로드
  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/home`);
      const data = await response.json();

      if (data.success) {
        let loadedContents = data.data || [];
        
        // title 섹션이 없으면 추가
        if (!loadedContents.find(c => c.section_type === 'title')) {
          loadedContents.push({
            ID: 'new_title',
            section_type: 'title',
            title: null,
            content: 'KSC 2019 겨울 학회',
            link: null,
            display_order: 1,
            is_active: 1,
            isNew: true
          });
        }
        
        // image 섹션이 없으면 추가
        if (!loadedContents.find(c => c.section_type === 'image')) {
          loadedContents.push({
            ID: 'new_image',
            section_type: 'image',
            title: null,
            content: `${config.images.serverUrl}${config.images.basePath}${config.images.homeLogo}`,
            link: null,
            display_order: 2,
            is_active: 1,
            isNew: true
          });
        }
        
        // intro 섹션이 없으면 추가
        if (!loadedContents.find(c => c.section_type === 'intro')) {
          loadedContents.push({
            ID: 'new_intro',
            section_type: 'intro',
            title: null,
            content: '',
            link: null,
            display_order: 3,
            is_active: 1,
            isNew: true
          });
        }
        
        setContents(loadedContents);
      }
    } catch (err) {
      console.error('Error fetching home contents:', err);
      setError('Failed to load contents');
    } finally {
      setLoading(false);
    }
  };

  // 컨텐츠 변경
  const handleContentChange = (id, field, value) => {
    setContents(contents.map(c => 
      c.ID === id ? { ...c, [field]: value } : c
    ));
  };

  // 모두 저장
  const handleSaveAll = async () => {
    // 이미지 업로드 (선택된 파일이 있을 때만)
    const uploadedLink = await imageUploadRef.current?.upload();
    if (uploadedLink === false) return; // 업로드 실패

    // 업로드 성공 시 contents의 image 항목 content를 전체 URL로 교체
    const finalContents = uploadedLink
      ? contents.map(ct =>
          ct.section_type === 'image'
            ? { ...ct, content: `${config.images.serverUrl}${config.images.basePath}${uploadedLink}` }
            : ct
        )
      : contents;

    try {
      setSaving(true);
      setError(null);

      // 각 컨텐츠 저장
      for (const content of finalContents) {
        const payload = {
          section_type: content.section_type,
          title: content.title,
          content: content.content,
          link: content.link,
          display_order: content.display_order,
          is_active: content.is_active
        };

        if (content.isNew) {
          // 새 컨텐츠 추가
          await fetch(`${API_URL}/home`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } else {
          // 기존 컨텐츠 업데이트
          await fetch(`${API_URL}/home/${content.ID}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      console.error('Error saving contents:', err);
      setError('Failed to save contents');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <h2 style={{ color: 'green' }}>Home 페이지가 성공적으로 갱신되었습니다.</h2>
        <p>Home 페이지로 돌아가는 중...</p>
      </div>
    );
  }

  const title = contents.find(c => c.section_type === 'title');
  const image = contents.find(c => c.section_type === 'image');
  const intro = contents.find(c => c.section_type === 'intro');

  return (
    <div className="site-content">
      <h2 className="section-title">Edit Home Page</h2>

      {error && (
        <div style={{ padding: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Image Upload */}
        <section style={{ marginBottom: '40px' }}>
          <h3 style={{ marginBottom: '15px' }}>이미지</h3>
          {image && (
            <ImageDropUpload
              ref={imageUploadRef}
              uploadDir="home"
              imageBaseUrl=""
              currentImageLink={image.content || ''}
            />
          )}
        </section>

        {/* Title */}
        <section style={{ marginBottom: '40px' }}>
          <h3 style={{ marginBottom: '15px' }}>이미지 제목</h3>
          {title && (
            <input
              type="text"
              value={title.content || ''}
              onChange={(e) => handleContentChange(title.ID, 'content', e.target.value)}
              placeholder="{학술대회명} {YYYY} {계절명} 학회"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          )}
        </section>

        {/* Intro Section */}
        <section style={{ marginBottom: '40px' }}>
          <h3 style={{ marginBottom: '15px' }}>설명</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Enter를 2번 눌러 자동으로 문단을 구분합니다.
          </p>
          {intro && (
            <textarea
              value={intro.content || ''}
              onChange={(e) => handleContentChange(intro.ID, 'content', e.target.value)}
              rows="12"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                resize: 'vertical'
              }}
            />
          )}
        </section>

        {/* Save Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#ddd',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            취소
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            style={{
              padding: '12px 30px',
              fontSize: '16px',
              backgroundColor: '#0066cc',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : '변경 사항 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditHome;
